import { Component, Input, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';
import { AppMainService } from 'src/app/app-main.service';
import { AddCommentPayload, Comments, WBChildComment, WBCommentCategory, WBCreateCommentCategory, WBParentComment } from '../models/workbook-comment.models';
import { WorkbookCommentService } from '../services/workbook-comment.services';
import { AppMainComponent } from 'src/app/app.main.component';
import "quill-mention/autoregister";
import { DomSanitizer } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';
import { LogAttachment } from 'src/app/shared/model/logAttachment';
import { uploadFiles, validateFiles, validateFileSize } from 'src/app/shared/upload-files';
import { DocumentManagementService } from 'src/app/modules/shared/services/document-management/document-management.service';
import { Status } from 'src/app/models/statusEnums';
import { WBPermissionType } from '../enum/workbook-permission.enum';

@Component({
  selector: 'app-workbook-comment',
  templateUrl: './workbook-comment.component.html',
  styleUrl: './workbook-comment.component.scss'
})
export class WorkbookCommentComponent {

  @Input() rowNumber: number;
  @Input() workbookId: string;
  @Input() updateReadStatus: boolean = false;
  @Input() permissionType?: WBPermissionType= WBPermissionType.Read;
    /** Subscription and Cleanup */
  protected ngUnsubscribe$: Subject<void> = new Subject<void>();

  WBPermissionType=WBPermissionType;  
  workbookComments: Comments[] = [];
  wbParentComments: WBParentComment[] = [];
  wbChildComments: WBChildComment[] = [];

  wbCommentCategories: WBCommentCategory[] = [];

  commentValidationError: string = '';
  isCommentEdit = false;
  selectedComment: Comments;
  newComment: string = '';
  isUpdating: boolean = false;
  commentHeader: string
  displayAddComment: boolean = false;
  commentForm!: FormGroup;
  subcategories: any[] = [];
  selectedUserId: any;
  uploadedFiles: any[] = [];
  logAttachment: LogAttachment = {
          id: 0,
          createdSource: this.app.userIP,
          createdBy: this.app.user.userId,
          createdDate: new Date(),
          logId: 0,
          attachmentId: 0,
          attachment: {
              id: 0,
              createdSource: this.app.userIP,
              createdBy: this.app.user.userId,
              createdDate: new Date(),
              name: "",
              guidName: "",
              content: "",
              extension: "",
              sizeInBytes: 0,
              description: "",
              statusId: 0,
              isValid: true,
              validFromDate: null,
              validToDate: null,
          },
      };

  logTemp:  any = {
    log: {
      logAttachments: [],
    },
  };

  /** Comment Details popup */
  displayCommentDetailPopup: boolean = false;
  selectedParentComment: WBParentComment;
  users = [];
  multiLineTooltip = `<ul class="p-mt-0 p-pl-4 p-mb-0">
  <li>Files with extensions: gif, png, jpg, jpeg, pdf, txt, xls, csv, xlsx, doc, docx, ppt, pptx are allowed</li>
  <li>Each File size should not be more than 10MB</li>
  <li>You can upload a maximum of 5 files at a time</li>
</ul>`;

allowedExtensions = [
  'pdf',
  'csv',
  'txt',
  'xls',
  'xlsx',
  'doc',
  'docx',
  'png',
  'jpg',
  'jpeg',
  'ppt',
  'pptx',
  'gif'
]; // For the attachment files


  /**Quill config */
  quillConfig={
    toolbar: false,
    mention: {
      allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
      mentionDenotationChars: ["@"],
      showDenotationChar: false,
      spaceAfterInsert: false,
      source: (searchTerm, renderList, mentionChar) => {
        let values = this.users || [];

        if (searchTerm.length === 0) {
          renderList(values, searchTerm);
        } else {
          const matches = values.filter(user =>
            user.value?.toLowerCase().includes(searchTerm.toLowerCase())
          );
          renderList(matches, searchTerm);
        }
      },
    },
  }

  //user details
  displayUserDetails = false;
  isUploading: boolean = false;

  constructor(
    public app: AppMainComponent,
    public readonly workbookCommentService: WorkbookCommentService,
    private readonly appMainService: AppMainService,
    private readonly confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    private readonly documentManagementService: DocumentManagementService,
  ) {
    this.initializeCommentForm();
    const users = this.app.usersList;
    this.users = users.map(user => ({ id: user.id, value: user.name }));
   }

  initializeCommentForm() {
    this.commentForm = this.fb.group({
      category: [null, Validators.required],
      subcategory: [null],
      comment: ['', [Validators.required, Validators.maxLength(5000)]],
      attachments: this.fb.array([]) 
    },
    {
      validators: [this.maxPlainTextLengthValidator(1000).bind(this)], // Applying as a form-level validator
    }
  );

    this.commentForm.get('category')?.valueChanges.subscribe((categoryId) => {
      this.updateSubcategories(categoryId);
    });

  }
  openAddEditComment(){
    this.getWBCommentCategory();
    this.displayAddComment = true;
    this.refreshCommentAttachemnt();
  }
  refreshCommentAttachemnt(){
    this.logTemp = {
      log: {
        logAttachments: [],
      },
    };
 
  }
  onCategoryChange(event: any): void {
    const categoryId = event.value.id;
    if(event.value.childCategories.length > 0) {
      this.subcategories = event.value.childCategories;
    } else {
      this.subcategories = [];
    }
    this.updateSubcategories(categoryId);
  }

  updateSubcategories(categoryId: string): void {
    // this.subcategories = this.allSubcategories[categoryId] || [];
    const subcategoryControl = this.commentForm.get('subcategory');
    if (this.subcategories.length > 0) {
      subcategoryControl?.setValidators(Validators.required);
    } else {
      subcategoryControl?.clearValidators();
      this.subcategories = [];
    }
    subcategoryControl?.updateValueAndValidity();
    subcategoryControl?.reset(); // Reset the value when the category changes
  }

  onSubmit(): void {
    if (this.commentForm.invalid){
      this.commentForm.markAllAsTouched(); // Mark all controls as touched
      return;
    }
    if (this.commentForm.valid) {
      const mentionUsers = this.getMentions(this.commentForm.get('comment')?.value);
      const selectedUserIds = mentionUsers.map(user => user.id);
    
      const addCommentPayload: any = {
        tableId: this.workbookId,
        rowNumber: this.rowNumber,
        value: this.commentForm.get('comment')?.value,
        categoryId: this.commentForm.get('category')?.value?.id,
        subCategoryId: this.commentForm.get('subcategory')?.value?.id || '',
        mentionedUserIds: selectedUserIds?.length > 0 ? selectedUserIds.map(userId => userId.toString()) : [],
        attachment: (this.commentForm.get('attachments')?.value || []).map(file => ({
          name: file.fileName,
          sizeInKB: file.fileSizeInKB,
          attachmentId: file.id,
        }))
      };  
      this.addComment(addCommentPayload);
      this.displayAddComment = false;
    }
  }

  hideAddComment(): void {
    this.commentForm.reset();
    this.attachments.clear();
    this.displayAddComment = false;
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rowNumber'] && changes['rowNumber'].currentValue !== changes['rowNumber'].previousValue) {
      this.getWBParentComment(this.rowNumber);
    }
  }
  ngAfterViewInit() {
    document.addEventListener('click', (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const mentionElement = target.closest('.mention');
  
      if (mentionElement) {
        event.stopPropagation(); // Stop event bubbling
        const userId = mentionElement.getAttribute('data-id');
        const userName = mentionElement.getAttribute('data-value');

        if(userId!==this.selectedUserId){
        this.openTaggedUserDetails(userId);
      }}
    });
  }
  

  getWorkbookRowComment(rowNumber) {
    this.appMainService.isShowSpinner('getWorkbookRowComment');
    this.workbookCommentService.getWorkbookRowComment(this.workbookId, rowNumber).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (response: any) => {
        this.appMainService.isHideSpinner('getWorkbookRowComment');
        this.workbookComments = response;
      },
      error: (error) => {
        this.appMainService.isHideSpinner('getWorkbookRowComment');
      }
    })
  }

  updateParentCommentStatus(ParentCommentId: string) {
    const isRead: boolean = true;
    this.appMainService.isShowSpinner('updateParentCommentStatus');
    this.workbookCommentService.updateParentCommentStatus(this.workbookId, isRead, this.rowNumber).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (response: any) => {
        this.appMainService.isHideSpinner('updateParentCommentStatus');
      },
      error: (error) => {
        this.appMainService.isHideSpinner('updateParentCommentStatus');
      }
    })
  }

  getWBParentComment(rowNumber) {
    this.appMainService.isShowSpinner('getWBParentComment');
    this.workbookCommentService.getWBParentComment(this.workbookId, rowNumber).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (response: any) => {
        this.appMainService.isHideSpinner('getWBParentComment');
        const responses= response.map(comment => (
          { ...comment, latestContent: this.sanitizer.bypassSecurityTrustHtml(comment.latestContent), }));

        this.wbParentComments = responses;
        if(this.updateReadStatus){
          this.updateParentCommentStatus(rowNumber);
          this.updateReadStatus = false;
        }
      },
      error: (error) => {
        this.appMainService.isHideSpinner('getWBParentComment');
      }
    })
  }

  getWBChildComment(rowNumber, parentId) {
    this.appMainService.isShowSpinner('getWorkbookRowComment');
    this.workbookCommentService.getWBChildComment(this.workbookId, rowNumber, parentId).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (response: any) => {
        this.appMainService.isHideSpinner('getWorkbookRowComment');
        this.workbookComments = response;
      },
      error: (error) => {
        this.appMainService.isHideSpinner('getWorkbookRowComment');
      }
    })
  }

  getWBCommentCategory() {
    this.appMainService.isShowSpinner('getWBCommentCategory');
    this.workbookCommentService.getCommentCategory().pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (response: any) => {
        this.appMainService.isHideSpinner('getWBCommentCategory');
        this.wbCommentCategories = response;
      },
      error: (error) => {
        this.appMainService.isHideSpinner('getWBCommentCategory');
      }
    })
  }

   createCommentCategory(wbCreateCommentCategory: WBCreateCommentCategory) {
    this.appMainService.isShowSpinner('createCommentCategory');
    this.workbookCommentService.createCommentCategory(wbCreateCommentCategory).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (response: any) => {
        this.appMainService.isHideSpinner('createCommentCategory');
        this.workbookComments = response;
      },
      error: (error) => {
        this.appMainService.isHideSpinner('createCommentCategory');
      }
    })
  }

  updateCommentCategory(wbUpdateCommentCategory: any) {
    this.appMainService.isShowSpinner('updateCommentCategory');
    this.workbookCommentService.updateCommentCategory(wbUpdateCommentCategory).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (response: any) => {
        this.appMainService.isHideSpinner('updateCommentCategory');
        this.workbookComments = response;
      },
      error: (error) => {
        this.appMainService.isHideSpinner('updateCommentCategory');
      }
    })
  }

  deleteCommentCategory(commentCategoryId: string) {
    this.appMainService.isShowSpinner('deleteCommentCategory');
    this.workbookCommentService.deleteCommentCategory(commentCategoryId).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (response: any) => {
        this.appMainService.isHideSpinner('deleteCommentCategory');
        this.workbookComments = response;
      },
      error: (error) => {
        this.appMainService.isHideSpinner('deleteCommentCategory');
      }
    })
  }

  addComment(addCommentPayload: any) {
    this.isUpdating = true;

    // API call to update the table name
    this.workbookCommentService.createWorkbookRowComment(this.workbookId, addCommentPayload, this.rowNumber)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(
        {
          next: (response: any) => {
            this.isUpdating = false;  // Reset the flag once the API call is successful
            this.newComment = '';  // Clear the comment field
            this.commentForm.reset();
            this.getWBParentComment(this.rowNumber);
            // this.getWorkbookRowComment(this.rowNumber);
          },
          error: (error) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.Message || 'Failed to add comment!' });
            this.isUpdating = false;  // Reset the flag if the API call fails
          }
        }
      );
  }

  editComment(comment: Comments) {
    this.newComment = comment?.value;
    this.isCommentEdit = true;
    this.selectedComment = comment;
  }

  updateComment() {
    // Check if validation passes
    if (!this.validateComment()) {
      return;  // Don't proceed if validation fails
    }
    // Check if API is already running
    if (this.isUpdating) {
      return;  // Prevent multiple API calls
    }

    // Set flag to indicate API call is in progress
    this.isUpdating = true;

    let updateCommentPayload = {
      commentId: this.selectedComment?.commentId,
      value: this.newComment,
    };

    // this.isEditHeader = false;

    // API call to update the table name
    this.workbookCommentService.updateWorkbookRowComment(this.workbookId, updateCommentPayload, this.rowNumber)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(
        {
          next: (response: any) => {
            this.isUpdating = false;  // Reset the flag once the API call is successful
            this.newComment = '';  // Clear the comment field
            this.getWBParentComment(this.rowNumber);

            // this.getWorkbookRowComment(this.rowNumber);
            this.isCommentEdit = false;
            this.selectedComment = null;
          },
          error: (error) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.Message || 'Failed to update comment!' });
            this.isUpdating = false;  // Reset the flag if the API call fails
          }
        }
      );
  }

  deleteComment(selectedComments: Comments) {
    this.confirmationService.confirm({
      message: 'Do you want to delete this Comment?',
      header: 'Delete Confirmation',
      icon: 'pi pi-info-circle',
      key: 'deleteComment',
      accept: () => {
        this.appMainService.isShowSpinner('deleteComment');
        this.workbookCommentService.deleteWorkbookRowComment(this.workbookId, selectedComments.commentId, this.rowNumber).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
          next: (response: any) => {
            this.appMainService.isHideSpinner('deleteComment');
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Comment deleted successfully!' });
            // this.getWorkbookRowComment(this.rowNumber);
            this.getWBParentComment(this.rowNumber);

          },
          error: (error) => {
            this.appMainService.isHideSpinner('deleteComment');
            this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.Message || 'Failed to delete comment!' });
          }
        })
      },
      reject: () => { },
    });

  }


  getInitials(name: string): string {
    if (!name || name.trim().length === 0) {
      return 'U';
    }

    const names = name.trim().split(/\s+/); // Split by spaces and handle extra spaces
    if (names.length === 1) {
      return names[0][0].toUpperCase();
    } else if (names.length === 2) {
      return (names[0][0] + names[1][0]).toUpperCase();
    } else {
      return (names[0][0] + names[1][0]).toUpperCase(); // First and second initials for 3+ names
    }
  }

  validateComment() {
    // Check if the table name is blank or only has spaces
    if (!this.newComment || this.newComment.trim().length === 0) {
      this.commentValidationError = 'Comment cannot be blank or contain only spaces.';
      return false;
    }

    // Check if table name starts with a space
    if (this.newComment.startsWith(' ')) {
      this.commentValidationError = 'Comment cannot start with a space.';
      return false;
    }

    // Check if the length exceeds 1000 characters
    if (this.newComment.length > 1000) {
      this.commentValidationError = 'Comment should be less than 1000 characters.';
      return false;
    }

    // If everything is valid
    this.commentValidationError = '';
    return true;
  }

  updateWorkbookRowCommentStatus() {
    const isRead: boolean = true;
    this.appMainService.isShowSpinner('updateWorkbookRowCommentStatus');
    this.workbookCommentService.updateWorkbookRowCommentStatus(this.workbookId, isRead, this.rowNumber).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (response: any) => {
        this.appMainService.isHideSpinner('updateWorkbookRowCommentStatus');
      },
      error: (error) => {
        this.appMainService.isHideSpinner('updateWorkbookRowCommentStatus');
      }
    })
  }

  openCommentDetail(comment, event){
    const target = event.target as HTMLElement;
    const mentionElement = target.closest('.mention');
    if (mentionElement) {
      return
    }
    this.selectedParentComment = comment;
    this.selectedParentComment.isRead = true;
    this.displayCommentDetailPopup = true;
  }

  hideCommentDetails(){
    this.displayCommentDetailPopup = false;
    this.selectedParentComment = null;
    this.getWBParentComment(this.rowNumber);
  }

  getMentions(comment: string) {
    const mentionRegex = /<span class="mention"[^>]*data-id="(.*?)"[^>]*data-value="(.*?)"[^>]*>/g;
    let mentions: { id: number; name: string }[] = [];
    let uniqueMentionsMap = new Map<number, { id: number; name: string }>(); // Map to store unique mentions by id
    let match;

    while ((match = mentionRegex.exec(comment)) !== null) {
      const id = parseInt(match[1], 10); // Convert id to number
      const name = match[2]; // Extracted mention name

      // Add to the Map to ensure uniqueness based on id
      uniqueMentionsMap.set(id, { id, name });
    }

    mentions = Array.from(uniqueMentionsMap.values());

    return mentions;
  }

/**
   * Custom form validator to check the length of plain text (ignoring HTML tags)
   */
    maxPlainTextLengthValidator(maxLength: number): ValidatorFn {
      return (formGroup: AbstractControl): ValidationErrors | null => {
        const commentControl = formGroup.get('comment');
        if (commentControl && commentControl.value) {
          const plainTextLength = this.countPlainTextLength(commentControl.value);
          if (plainTextLength > maxLength) {
            return { maxPlainTextLength: { requiredLength: maxLength, actualLength: plainTextLength } };
          }
        }
        return null;
      };
    }

/**
 * Method to count the length of plain text from an HTML string (ignoring HTML tags)
 */
private countPlainTextLength(htmlString: string | null): number {
  if (!htmlString) return 0;
  const tempElement = document.createElement('div');
  tempElement.innerHTML = htmlString;
  return tempElement.textContent?.length || 0;
}

/**
 * Getter for checking validation errors
 */
get isCommentInvalid() {
  return this.commentForm.hasError('maxPlainTextLength');
}

get attachments(): FormArray {
  return this.commentForm.get('attachments') as FormArray;
}

uploadAttachments(event: any, fileUpload) {
  if (event?.files&& event?.files?.length>5) {
    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Maximum 5 files are allowed at once!' });
    fileUpload.clear();
    return;
  }
  let errors = [];
  for (let file of event.files) {      
    this.appMainService.isShowSpinner('upload')
    errors = validateFiles(file, 10, this.allowedExtensions);
    if (errors.length) {
        for (let error of errors) {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: error.fileName + ": " + error.error
            });
        }
        this.appMainService.isHideSpinner('upload');
    }
    this.appMainService.isHideSpinner('upload');    }
    if(errors.length){
      fileUpload.clear();
      return;
    }
  const uploadedFiles:File[] = Array.from(event.files || []); // Convert FileList to an array
  if (!Array.isArray(uploadedFiles)) {
    return;
  }
  const path = `workbook/${this.workbookId}/row-${this.rowNumber}/comment`;
  fileUpload.clear();
  this.bulkUploadDocument(uploadedFiles, path, 'attachments');
}

bulkUploadDocument(files: File[], path: string, fieldId) {
  this.isUploading = true; // Disable the button
  this.appMainService.isShowSpinner('bulkUploadDocument');
  this.documentManagementService.bulkUploadDocument(files, path)
.pipe(takeUntil(this.ngUnsubscribe$))
.subscribe({
  next: (response: any) => {
    this.appMainService.isHideSpinner('bulkUploadDocument');
    this.isUploading = false; // Enable the button again
    if (response) {
      this.updateFormWithFiles(response, fieldId);
    }
  },
  error: (error) => {
    this.appMainService.isHideSpinner('bulkUploadDocument');
    this.isUploading = false; // Enable the button again on error
    this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.Message || 'Failed to upload documents!' });
  }
});

} 

 // Update form control with API response
 updateFormWithFiles(files: any[], fieldId: string) {
  const formArray = this.commentForm.get(fieldId) as FormArray;

  // Remove null or undefined values from the existing FormArray
  const validControls = formArray.controls.filter(control => control.value !== null && control.value !== undefined);
  formArray.clear(); // Clear the existing FormArray
  validControls.forEach(control => formArray.push(control)); // Add back only valid values

  files.forEach(file => {
    if (file !== null && file !== undefined) {
      formArray.push(new FormControl(file));
      this.logAttachment.attachment = {
        id: file.id,
        content: null, 
        name: file?.fileName,
        extension: null, //Need to changes
        isValid: true,
        sizeInBytes: file.fileSizeInKB,
        statusId: 1,
        createdBy: this.app.user.userId,
        createdDate: new Date(),
        createdSource: this.app.userIP,
        location: file.location,
      };
      this.logTemp.log.logAttachments.push(
        this.app.copyObjectWithoutRefrence(
          this.logAttachment
        )
      );
    }
  });

}

  removeAttachment(attachments: any) {
     const deletedAttachment = attachments.find(att => att.attachment.statusId === Status.Deleted);
     const deletedIndex = attachments.findIndex(att => att.attachment.statusId === Status.Deleted);
     this.logTemp.log.logAttachments.splice(deletedIndex, 1);
    this.deleteDocument(deletedAttachment, 'attachments', deletedIndex);
  }

  deleteDocument(selectedFile: any, column, index) {
    this.appMainService.isShowSpinner('deleteDocument');
    this.documentManagementService.deleteDocument(selectedFile?.attachment?.id).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (response: any) => {
        this.appMainService.isHideSpinner('deleteDocument');
        const formArray = this.commentForm.get(column) as FormArray;
        formArray.removeAt(index);        
      },
      error: (error) => {
        this.appMainService.isHideSpinner('deleteDocument');
      }
    })
  }


  getFileIcon(fileName: string): string {
    const seperationIndex = fileName.lastIndexOf(".");
    const extension = fileName.substring(seperationIndex + 1).toLowerCase();
    return `${environment.assestsBasePath}icons/${extension}.png`;
  }

  //#region Tagged User Details Popup
  openTaggedUserDetails(userId: any) {
    this.selectedUserId = userId;
    this.displayUserDetails = true;
  }
  closeTaggedUserDetails(){
    this.selectedUserId = null;
    this.displayUserDetails = false;
  }
  //#endregion Tagged User Details Popup  

  // Unsubscribe from all subscriptions
  ngOnDestroy() {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
